import GeckoRequest from 'services/gecko-request';
import { reduxStore } from 'services/gecko-store/redux';

const dispatch = (event, payload = {}, metadata = {}) => {
  return reduxStore.dispatch({
    type: event,
    payload,
    metadata,
  });
};

export const fetchAnswers = ({
  page,
  order,
  orderBy,
  botId,
  search,
  filters,
} = {}) => {
  const fullState = reduxStore.getState();
  const state = fullState.botsRoute.answers;
  page = page || state.page;
  order = order || state.order;
  orderBy = orderBy || state.orderBy;
  search = search !== undefined && search !== null ? search : state.search;
  filters = filters || state.filters;

  dispatch('STARTED_FETCHING_ANSWERS', {
    page,
    order,
    orderBy,
    botId,
    search,
    filters,
  });
  return GeckoRequest.get(`/bots/${botId}/answers`, {
    page,
    orderBy,
    order,
    search,
    filters: JSON.stringify(filters),
  })
    .then((payload) => {
      const { data, page, nextPage, total } = payload;
      dispatch('FETCHED_ANSWERS', { data, page, nextPage, total });
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_ANSWERS', { error: err });
    });
};

export const fetchAnswer = (id, botId, clone) => {
  dispatch('STARTED_FETCHING_ANSWER', { id, botId });
  return GeckoRequest.get(
    `/bots/${botId}/answers/${clone ? 'clone/' : ''}${id}`
  )
    .then((payload) => {
      dispatch('FETCHED_ANSWER', { id, data: payload });
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_ANSWER', { error: err });
    });
};

export const saveAnswer = (answer, botId) => {
  return new Promise((resolve, reject) => {
    // Handle updating an answer
    if (answer.id !== 'new') {
      dispatch('STARTED_UPDATING_ANSWER', { botId, answer });
      return GeckoRequest.post(`/bots/${botId}/answers/${answer.id}`, answer)
        .then((answer) => {
          dispatch('UPDATED_ANSWER', { answer });
          return resolve(answer);
        })
        .catch((err) => {
          dispatch('FAILED_UPDATING_ANSWER', { error: err, answer });
          return reject(err);
        });
    }
    // Handle creating an answer
    dispatch('STARTED_CREATING_ANSWER', { botId, answer });
    return GeckoRequest.post(`/bots/${botId}/answers`, answer)
      .then((answer) => {
        dispatch('CREATED_ANSWER', { answer });
        return resolve(answer);
      })
      .catch((err) => {
        dispatch('FAILED_CREATING_ANSWER', { error: err });
        return reject(err);
      });
  });
};

export const fetchSuggestedOpportunitiesAndMisses = ({
  page,
  order,
  orderBy,
  botId,
  search,
  filters,
}) => {
  const fullState = reduxStore.getState();
  const { selectedOpportunity } = fullState.botsRoute;
  return GeckoRequest.get(`/bots/${botId}/suggested_opportunities`).then(
    (suggestedOpportunities) => {
      const shouldSelectedOpportunityBeReset =
        selectedOpportunity &&
        !suggestedOpportunities.find(
          (opportunity) =>
            opportunity.closest_response ===
            selectedOpportunity.closest_response
        );
      let updatedFilters = filters;
      if (shouldSelectedOpportunityBeReset) {
        updatedFilters = filters.filter(
          (filter) => filter.key !== 'closest_response'
        );
        dispatch('SET_SELECTED_OPPORTUNITY', null);
      }
      dispatch('SET_SUGGESTED_OPPORTUNITIES', suggestedOpportunities);
      fetchMisses({
        page,
        order,
        orderBy,
        botId,
        search,
        filters: updatedFilters,
      });
    }
  );
};

export const fetchMisses = ({
  page,
  order,
  orderBy,
  botId,
  search,
  filters,
} = {}) => {
  const fullState = reduxStore.getState();
  const state = fullState.botsRoute.misses;
  page = page || state.page;
  order = order || state.order;
  orderBy = orderBy || state.orderBy;
  filters = filters || state.filters;
  search = search !== undefined && search !== null ? search : state.search;
  dispatch('STARTED_FETCHING_MISSES', {
    page,
    order,
    orderBy,
    botId,
    search,
    filters,
  });
  return GeckoRequest.get(`/bots/${botId}/misses`, {
    page,
    orderBy,
    order,
    search: JSON.stringify(search),
    filters: JSON.stringify(filters),
  })
    .then((payload) => {
      const { data, page, nextPage, total } = payload;
      dispatch('FETCHED_MISSES', { data, page, nextPage, total });
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_MISSES', { error: err });
    });
};

export const fetchSkills = ({
  page,
  order,
  orderBy,
  botId,
  search,
  filters,
} = {}) => {
  const fullState = reduxStore.getState();
  const state = fullState.botsRoute.skills;
  page = page || state.page;
  order = order || state.order;
  orderBy = orderBy || state.orderBy;
  search = search !== undefined && search !== null ? search : state.search;
  filters = filters || state.filters;

  dispatch('STARTED_FETCHING_SKILLS', {
    page,
    order,
    orderBy,
    botId,
    search,
    filters,
  });
  return GeckoRequest.get(`/bots/${botId}/skills`, {
    page,
    orderBy,
    order,
    search,
    filters: JSON.stringify(filters),
  })
    .then((payload) => {
      const { data, page, nextPage, total } = payload;
      dispatch('FETCHED_SKILLS', { data, page, nextPage, total });
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_SKILLS', { error: err });
    });
};

export const fetchBots = ({ page, order, orderBy } = {}) => {
  const fullState = reduxStore.getState();
  const state = fullState.botsRoute.bots;
  page = page || state.page;
  order = order || state.order;
  orderBy = orderBy || state.orderBy;
  dispatch('STARTED_FETCHING_BOTS', { page, order, orderBy });
  return GeckoRequest.get('/bots', { page, orderBy, order })
    .then((payload) => {
      const { data, page, nextPage, total } = payload;
      dispatch('FETCHED_BOTS', { data, page, nextPage, total });
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_BOTS', { error: err });
    });
};

export const addNewBot = (bot) => {
  dispatch('ADDED_NEW_BOT', { bot });
};

export const fetchConversationSnippet = ({
  message,
  direction,
  numberOfResults = 1,
  getLastBotMessage = false,
  ignoreConversationEvents = false,
} = {}) => {
  return GeckoRequest.get(
    `/message/${message.conversation_id}/${direction}/${
      message.received_at
    }?perPage=${numberOfResults}${
      getLastBotMessage ? '&getLastBotMessage=true' : ''
    }${ignoreConversationEvents ? '&ignoreConversationEvents=true' : ''}`
  )
    .then((conversation) => {
      conversation.data.map((msg) => {
        msg.sender = {
          full_name:
            msg.first_name ||
            msg.additional.fake_first_name + ' ' + msg.last_name ||
            msg.additional.fake_last_name,
          first_name: msg.first_name || msg.additional.fake_first_name,
          last_name: msg.last_name || msg.additional.fake_last_name,
          type: msg.type,
          additional: msg.additional,
          status: 'read',
        };
        msg.sender_channel = {
          name: msg.name,
          type: msg.channel_type,
        };
        return msg;
      });
      return conversation;
    })
    .then((conversation) => {
      return conversation;
    });
};

export const fetchImportDeltas = ({
  page,
  order,
  orderBy,
  botId,
  importId,
} = {}) => {
  const fullState = reduxStore.getState();
  const state = fullState.botsRoute.deltas;
  page = page || state.page;
  order = order || state.order;
  orderBy = orderBy || state.orderBy;

  dispatch('STARTED_FETCHING_DELTAS', { page, order, orderBy, botId });
  return GeckoRequest.get(`/bots/${botId}/imports/${importId}/deltas`, {
    page,
    orderBy,
    order,
    status: 'NOT_REVIEWED',
    perPage: 2,
  })
    .then((payload) => {
      const { data, page, nextPage, total } = payload;
      if (!data.length && page > 1) {
        return fetchImportDeltas({ page: page - 1, botId, importId });
      }
      dispatch('FETCHED_DELTAS', { data, page, nextPage, total });
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_DELTAS', { error: err });
    });
};

export const fetchImport = ({ botId, importId } = {}) => {
  const fullState = reduxStore.getState();
  const state = fullState.botsRoute.imports;
  dispatch('STARTED_FETCHING_IMPORTS', { botId, importId });
  return GeckoRequest.get(`/bots/${botId}/imports/${importId}`)
    .then((payload) => {
      const data = [payload];
      dispatch('FETCHED_IMPORTS', { data });
    })
    .catch((err) => {
      dispatch('FAILED_FETCHING_IMPORTS', { error: err });
    });
};
